import { io, type Socket } from 'socket.io-client'

// ─── Typed event payloads ──────────────────────────────────────────────────────
// Mirror the WebSocket Events Reference table in CLAUDE.md.
// Keep these in sync with the server-side types in wsServer.ts / terminalService.ts.

/** Server → Client */
export interface TerminalOutputPayload  { sessionId: string; data: string }
export interface TerminalBlockedPayload { reason: string }
export interface TerminalErrorPayload   { message: string }
export interface TerminalExitPayload    { sessionId: string; exitCode: number }
export interface LessCompiledPayload    { lessFile: string; cssFile: string }
export interface LessErrorPayload       { file: string; error: string }
export interface FileChangedPayload     { path: string }

/** Client → Server */
export interface TerminalCreatePayload  { sessionId: string; cwd: string }
export interface TerminalInputPayload   { sessionId: string; input: string }
export interface TerminalResizePayload  { sessionId: string; cols: number; rows: number }

// ─── Window global injected by index.html (Twig) ──────────────────────────────

declare global {
  interface Window {
    /**
     * Short-lived session token generated by the Craft PHP layer and injected
     * into the page before serving. Sent in the Socket.io handshake so the
     * server can verify the connection belongs to an authenticated Craft user.
     */
    __CRAFT_SESSION_TOKEN__: string
  }
}

// ─── Singleton socket ─────────────────────────────────────────────────────────

let socket: Socket | null = null

/**
 * Return the singleton Socket.io connection, creating it on first call.
 *
 * Security: the Craft session token from window.__CRAFT_SESSION_TOKEN__ is
 * passed in the handshake `auth` object. wsServer.ts validates it in the
 * io.use() middleware before any event can fire. An invalid or expired token
 * results in a `connect_error` with message "Unauthorized".
 *
 * Transport: WebSocket only (`transports: ['websocket']`). The HTTP-polling
 * fallback is intentionally disabled because the nginx /ws location only
 * upgrades WebSocket connections — polling requests would receive no response.
 */
export function getSocket(): Socket {
  if (socket) return socket

  socket = io({
    path:                 '/ws',
    auth:                 (cb) => cb({ token: window.__CRAFT_SESSION_TOKEN__ ?? '' }),
    reconnectionAttempts: 10,
    reconnectionDelay:    1_000,
    reconnectionDelayMax: 10_000,  // cap exponential back-off at 10 s
    transports:           ['websocket'],
    autoConnect:          false,   // defer until connectSocket() is called after token is set
  })

  socket.on('connect', () => {
    console.debug('[wsClient] connected:', socket?.id)
  })

  socket.on('connect_error', (err: Error) => {
    // "Unauthorized" means the session token was rejected — the user should
    // reload the page to obtain a fresh token from the PHP layer.
    console.error('[wsClient] connection error:', err.message)
  })

  socket.on('disconnect', (reason: string) => {
    console.debug('[wsClient] disconnected:', reason)

    // 'io server disconnect' means the server deliberately closed the socket
    // (e.g. token expired mid-session). Trigger an explicit reconnect so the
    // retry logic re-runs the auth middleware with the current token.
    if (reason === 'io server disconnect') {
      socket?.connect()
    }
  })

  socket.io.on('reconnect', (attempt: number) => {
    console.debug(`[wsClient] reconnected after ${attempt} attempt(s)`)
  })

  socket.io.on('reconnect_failed', () => {
    console.error(
      `[wsClient] gave up after ${socket?.io.opts.reconnectionAttempts ?? 10} reconnection attempts`,
    )
  })

  return socket
}

/**
 * Initiate the WebSocket connection.
 * Must be called once after window.__CRAFT_SESSION_TOKEN__ has been set.
 * Safe to call multiple times — only connects if not already connected.
 */
export function connectSocket(): void {
  const s = getSocket()
  if (!s.connected) s.connect()
}

/** Whether the socket currently has an active connection. */
export function isConnected(): boolean {
  return socket?.connected ?? false
}

/**
 * Permanently tear down the WebSocket connection.
 * Call this on user logout so the token-validated socket is not left open.
 */
export function disconnectSocket(): void {
  socket?.disconnect()
  socket = null
}

// ─── Typed emit helpers ────────────────────────────────────────────────────────
// Centralise event-name strings here so callers never hard-code them.

/** Open a new PTY terminal session on the server. */
export function emitTerminalCreate(payload: TerminalCreatePayload): void {
  getSocket().emit('terminal:create', payload)
}

/** Send keyboard input to a running PTY session. */
export function emitTerminalInput(payload: TerminalInputPayload): void {
  getSocket().emit('terminal:input', payload)
}

/** Notify the server that the terminal viewport has been resized. */
export function emitTerminalResize(payload: TerminalResizePayload): void {
  getSocket().emit('terminal:resize', payload)
}

// ─── Typed subscribe helpers ───────────────────────────────────────────────────
// Each function registers a listener and returns a cleanup callback.
// Usage in Vue components:
//
//   onMounted(() => {
//     const off = onTerminalOutput(({ sessionId, data }) => { ... })
//     onUnmounted(off)
//   })

/** Listen for PTY output data destined for the terminal UI. */
export function onTerminalOutput(cb: (p: TerminalOutputPayload) => void): () => void {
  const s = getSocket()
  s.on('terminal:output', cb)
  return () => s.off('terminal:output', cb)
}

/** Listen for blocked-command notifications from the terminal security filter. */
export function onTerminalBlocked(cb: (p: TerminalBlockedPayload) => void): () => void {
  const s = getSocket()
  s.on('terminal:blocked', cb)
  return () => s.off('terminal:blocked', cb)
}

/** Listen for terminal error messages (e.g. invalid CWD). */
export function onTerminalError(cb: (p: TerminalErrorPayload | string) => void): () => void {
  const s = getSocket()
  s.on('terminal:error', cb)
  return () => s.off('terminal:error', cb)
}

/** Listen for PTY session exit events. */
export function onTerminalExit(cb: (p: TerminalExitPayload) => void): () => void {
  const s = getSocket()
  s.on('terminal:exit', cb)
  return () => s.off('terminal:exit', cb)
}

/** Listen for successful LESS compilation notifications from the file watcher. */
export function onLessCompiled(cb: (p: LessCompiledPayload) => void): () => void {
  const s = getSocket()
  s.on('less:compiled', cb)
  return () => s.off('less:compiled', cb)
}

/** Listen for LESS compilation error notifications. */
export function onLessError(cb: (p: LessErrorPayload) => void): () => void {
  const s = getSocket()
  s.on('less:error', cb)
  return () => s.off('less:error', cb)
}

/** Listen for file-change events pushed by the server-side file watcher. */
export function onFileChanged(cb: (p: FileChangedPayload) => void): () => void {
  const s = getSocket()
  s.on('file:changed', cb)
  return () => s.off('file:changed', cb)
}

// ─── Git ack helper ────────────────────────────────────────────────────────────
//
// All git operations use Socket.io acknowledgement callbacks so the caller gets
// a clean Promise<T> instead of having to manage separate response events.
//
// Server responds with:  { ok: true, data: T }  |  { ok: false, error: string }

/** Standard ack envelope returned by all git socket events. */
export type GitAck<T> = { ok: true; data: T } | { ok: false; error: string }

/**
 * Emit a git event and wrap the Socket.io ack callback in a Promise.
 * Rejects with an Error if the server reports ok: false.
 *
 * @param event   - Socket.io event name (e.g. 'git:status')
 * @param payload - Event payload object
 */
export function emitGit<T>(event: string, payload: object): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    getSocket().emit(event, payload, (ack: GitAck<T>) => {
      if (ack.ok) resolve(ack.data)
      else reject(new Error(ack.error))
    })
  })
}

// ─── Git subscribe helpers ─────────────────────────────────────────────────────

/** Server-pushed git status after any mutating operation (commit, checkout, etc.). */
export interface GitStatusPushPayload { repoPath: string; status: unknown }

/**
 * Listen for server-broadcast git status updates.
 * Emitted after any mutating git operation so all open tabs stay in sync.
 */
export function onGitStatusPush(cb: (p: GitStatusPushPayload) => void): () => void {
  const s = getSocket()
  s.on('git:status', cb)
  return () => s.off('git:status', cb)
}

// ─── Repo clone/pull status ────────────────────────────────────────────────────

export interface RepoStatusPayload {
  repoId:    number
  status:    'cloning' | 'ready' | 'error'
  localPath: string | null
}

/**
 * Listen for repo clone/pull status updates emitted by the Node.js RepoManager.
 * The browser uses these events to update local state and persist to PHP.
 */
export function onRepoStatus(cb: (p: RepoStatusPayload) => void): () => void {
  const s = getSocket()
  s.on('repo:status', cb)
  return () => s.off('repo:status', cb)
}

// ─── DB Import status ──────────────────────────────────────────────────────────

export interface DbImportStartPayload    { repoId: number }
export interface DbImportCompletePayload { repoId: number }
export interface DbImportErrorPayload    { repoId: number; error: string }

/** Listen for SQL import start (mysql process spawned). */
export function onDbImportStart(cb: (p: DbImportStartPayload) => void): () => void {
  const s = getSocket()
  s.on('db:import-start', cb)
  return () => s.off('db:import-start', cb)
}

/** Listen for successful SQL import completion. */
export function onDbImportComplete(cb: (p: DbImportCompletePayload) => void): () => void {
  const s = getSocket()
  s.on('db:import-complete', cb)
  return () => s.off('db:import-complete', cb)
}

/** Listen for SQL import errors (mysql exited non-zero). */
export function onDbImportError(cb: (p: DbImportErrorPayload) => void): () => void {
  const s = getSocket()
  s.on('db:import-error', cb)
  return () => s.off('db:import-error', cb)
}
